// @ts-nocheck
// TODO: Add typing
class SBPriceCalculator {
  constructor() {}
  static QUANTITIES = [50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000];

  static getBasePrice(material, design) {
    const {
      widthIn: designWidthIn,
      heightIn: designHeightIn,
      quantity,
    } = design;

    const {
      cost: materialCost,
      width: usableWidthIn,
      length: usableLengthFt,
      spacing: spacingIn,
    } = material;

    const { recommended } = this.calculateRollPrintingCost({
      design: {
        qty: Number(quantity),
        width: Number(designWidthIn),
        height: Number(designHeightIn),
        tileSpacing: Number(spacingIn),
        bleed: 0,
        wastage: 0,
      },
      media: {
        cost: Number(materialCost),
        usableWidth: Number(usableWidthIn),
        rollLength: Number(usableLengthFt),
      },
    });

    if (recommended.error) {
      throw new Error(recommended.error);
    }

    return recommended;
  }

  static getPriceFromTable(priceTable, design, quantities) {
    const { widthIn: w, heightIn: h, quantity: qty } = design;
    if (w <= 0 || h <= 0 || qty <= 0) return 0;

    const AREAS = Object.keys(priceTable)
      .map(Number)
      .sort((a, b) => a - b);
    if (AREAS.length === 0) return 0;

    const QTYS = quantities?.length ? quantities : this.QUANTITIES;

    const LOG_A = AREAS.map(Math.log);
    const LOG_Q = QTYS.map(Math.log);
    const LOG_P = {};
    for (const a of AREAS) {
      const row = priceTable[String(a)];
      if (!row?.length) return 0;
      LOG_P[a] = row.map(Math.log);
    }

    const lerp = (x, x0, x1, y0, y1) => {
      if (x1 === x0) return y0;
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    };

    const area = w * h;
    let la = Math.log(area);
    let lq = Math.log(qty);

    if (area <= AREAS[0]) la = LOG_A[0];
    if (area >= AREAS[AREAS.length - 1]) la = LOG_A[LOG_A.length - 1];
    if (qty <= QTYS[0]) lq = LOG_Q[0];
    if (qty >= QTYS[QTYS.length - 1]) lq = LOG_Q[LOG_Q.length - 1];

    let ai = 0;
    for (let i = 0; i < LOG_A.length - 1; i++) {
      if (la >= LOG_A[i] && la <= LOG_A[i + 1]) {
        ai = i;
        break;
      }
      if (i === LOG_A.length - 2) ai = i;
    }

    let qi = 0;
    for (let j = 0; j < LOG_Q.length - 1; j++) {
      if (lq >= LOG_Q[j] && lq <= LOG_Q[j + 1]) {
        qi = j;
        break;
      }
      if (j === LOG_Q.length - 2) qi = j;
    }

    const a0 = AREAS[ai];
    const a1 = AREAS[ai + 1] ?? AREAS[ai];
    const q0i = qi;
    const q1i = qi + 1 < LOG_Q.length ? qi + 1 : qi;

    const lp00 = LOG_P[a0][q0i];
    const lp01 = LOG_P[a0][q1i];
    const lp10 = LOG_P[a1][q0i];
    const lp11 = LOG_P[a1][q1i];

    const lp0 = lerp(lq, LOG_Q[q0i], LOG_Q[q1i], lp00, lp01);
    const lp1 = lerp(lq, LOG_Q[q0i], LOG_Q[q1i], lp10, lp11);
    const lp = lerp(la, LOG_A[ai], LOG_A[ai + 1] ?? LOG_A[ai], lp0, lp1);

    return Math.exp(lp);
  }

  static calculate({ material, design, extraMultiplier = 1 }) {
    // Base price per piece
    const { unitCost } = this.getBasePrice(material, design);

    // Price from price table
    let priceFromTable = this.getPriceFromTable(
      material.pricesByArea,
      design,
      material.pricesByAreaQuantities,
    );

    // If quantity is less than the first quantity in the price table, calculate the price of single piece and multiply by quantity
    if (design.quantity < material.pricesByAreaQuantities[0]) {
      priceFromTable = priceFromTable / material.pricesByAreaQuantities[0] * design.quantity;
    }

    // Discount
    const discount = this.findDiscount(material?.discounts, design.quantity);

    const basePrice = unitCost * design.quantity;
    const finalPrice = priceFromTable * (1 - discount / 100) * extraMultiplier;

    return {
      basePrice: this.round(basePrice),
      basePricePerPiece: this.round(unitCost),
      finalPrice: this.round(finalPrice),
      finalPricePerPiece: this.round(finalPrice / design.quantity),
    };
  }

  static calculateRollPrintingCost({ design, media }) {
    // Effective tile = design + bleed on both sides + spacing to the next tile
    const tileW = design.width + 2 * design.bleed + design.tileSpacing;
    const tileH = design.height + 2 * design.bleed + design.tileSpacing;

    const portrait = this.calcPrintingOrientation(design, media, tileW, tileH);
    const landscape = this.calcPrintingOrientation(design, media, tileH, tileW);

    const pErr = !!portrait.error;
    const lErr = !!landscape.error;

    let recommended;
    if (pErr && lErr) {
      recommended = { error: "Design is too big for both orientations" };
    } else if (pErr) {
      recommended = { layout: "Landscape", ...landscape };
    } else if (lErr) {
      recommended = { layout: "Portrait", ...portrait };
    } else if (landscape.totalCost <= portrait.totalCost) {
      recommended = { layout: "Landscape", ...landscape }; // ties go to Landscape (matches site)
    } else {
      recommended = { layout: "Portrait", ...portrait };
    }

    if (recommended.feetNeeded) {
      // @ts-ignore
      recommended.rollsNeeded = Math.ceil(
        recommended.feetNeeded / media.rollLength,
      );
    }

    return { portrait, landscape, recommended };
  }

  static calcPrintingOrientation(design, media, acrossDim, alongDim) {
    const designsPerRow = Math.floor(
      (media.usableWidth + design.tileSpacing) / acrossDim,
    );
    if (designsPerRow === 0) {
      return {
        error: "Media too small",
        inches: 0,
        feetNeeded: 0,
        totalCost: 0,
        unitCost: 0,
      };
    }
    const rowsNeeded = Math.ceil(design.qty / designsPerRow);

    let inches = rowsNeeded * alongDim;
    let feetNeeded = Math.ceil(inches / 12);

    if (design.wastage > 0) {
      const f = 1 + design.wastage / 100;
      inches = Math.ceil(inches * f);
      feetNeeded = Math.ceil(feetNeeded * f); // ceiled separately — quirk of the source
    }

    const totalCost = feetNeeded * (media.cost / media.rollLength);
    return {
      designsPerRow,
      rowsNeeded,
      inches,
      feetNeeded,
      totalCost,
      unitCost: design.qty > 0 ? totalCost / design.qty : 0,
    };
  }

  static findDiscount(discounts, quantity) {
    if (!Array.isArray(discounts) || discounts.length === 0) return 0;
    const qty = Number(quantity) || 0;
    const sorted = [...discounts]
      .filter(
        (d) =>
          d.minQty !== "" &&
          d.minQty != null &&
          d.discount !== "" &&
          d.discount != null,
      )
      .map((d) => ({ minQty: Number(d.minQty), discount: Number(d.discount) }))
      .sort((a, b) => b.minQty - a.minQty);
    const match = sorted.find((d) => qty >= d.minQty);
    return match ? match.discount : 0;
  }

  static round(value, decimals = 2) {
    return Number(Math.round(Number(`${value}e${decimals}`)) + `e-${decimals}`);
  }
}
